import React, { Suspense, useMemo, ReactNode } from 'react';
import { Switch, matchPath, useLocation } from 'react-router-dom';
import { Skeleton } from 'antd';

const routeFallback = <Skeleton active />;

/**
 * Renders the router's <Switch> inside a Suspense boundary that is *keyed by
 * the matched route path* (fixes APL-446).
 *
 * React 18's concurrent renderer (enabled by `createRoot`) does not re-render an
 * offscreen (suspended) subtree on a location change, and it can drop the retry
 * "ping" of a Suspense boundary when a navigation/redirect fires while a lazy
 * route chunk is still loading — react-router v5's synchronous location updates
 * are not concurrent-safe. With a single shared boundary the app then stays on
 * the fallback skeleton forever (stuck on first incognito login, where the
 * post-login SelectOrganization -> Home redirect overlaps with chunk loading;
 * a refresh fixes it because the warm cache removes the overlap).
 *
 * Keying the boundary by the matched route path gives each route its own
 * Suspense instance: navigating BETWEEN routes remounts a fresh boundary (so a
 * stranded one is never reused and the destination renders), while same-route
 * re-renders and in-app navigation under "/" keep the same key and never
 * remount. The match uses the same first-match-in-order semantics as the
 * <Switch> it wraps.
 */
export const RoutesWithSuspense: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();

  const matchedRouteKey = useMemo(() => {
    let key = 'unmatched';
    React.Children.forEach(children, (child) => {
      if (key !== 'unmatched' || !React.isValidElement(child)) {
        return;
      }
      const { path, exact, strict, from } = child.props as {
        path?: string | string[];
        exact?: boolean;
        strict?: boolean;
        from?: string;
      };
      const routePath = path ?? from;
      if (routePath && matchPath(location.pathname, { path: routePath, exact, strict })) {
        key = Array.isArray(routePath) ? routePath.join('|') : routePath;
      }
    });
    return key;
  }, [children, location.pathname]);

  return (
    <Suspense key={matchedRouteKey} fallback={routeFallback}>
      <Switch location={location}>{children}</Switch>
    </Suspense>
  );
};
